import { Filter, RotateCcw } from 'lucide-react'
import { Input, Label } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/Button'
import { hu } from '@/lib/i18n/hu'
import { useUsers } from '@/lib/api/hooks'
import type { CountryType, ListInvoicesParams, PaidBy, Source, WorkflowStatus } from '@/lib/api/types'

const STATUS_OPTIONS: WorkflowStatus[] = [
  'received',
  'waiting_for_nav',
  'matched',
  'discrepancy',
  'under_review',
  'approval_pending',
  'approved',
  'handed_to_accountant',
  'booked',
  'payment_pending',
  'paid',
  'disputed',
  'rejected',
  'archived',
]

const SOURCE_OPTIONS: Source[] = ['nav', 'email', 'manual']
const COUNTRY_OPTIONS: CountryType[] = ['HU', 'EU', 'THIRD_COUNTRY']

export function InvoiceFilters({
  value,
  onChange,
  onReset,
}: {
  value: ListInvoicesParams
  onChange: (next: ListInvoicesParams) => void
  onReset: () => void
}) {
  function toggleArray<T>(arr: T[] | undefined, item: T): T[] {
    const set = new Set(arr ?? [])
    if (set.has(item)) set.delete(item)
    else set.add(item)
    return Array.from(set)
  }
  const usersQ = useUsers()
  const users = usersQ.data ?? []

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 mb-4">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-3">
        <Filter className="h-4 w-4" /> {hu.common.filter}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label htmlFor="f-from">Kelt tól</Label>
          <Input
            id="f-from"
            type="date"
            value={value.from ?? ''}
            onChange={(e) => onChange({ ...value, from: e.target.value || undefined })}
          />
        </div>
        <div>
          <Label htmlFor="f-to">Kelt ig</Label>
          <Input
            id="f-to"
            type="date"
            value={value.to ?? ''}
            onChange={(e) => onChange({ ...value, to: e.target.value || undefined })}
          />
        </div>
        <div>
          <Label htmlFor="f-status">Workflow státusz</Label>
          <Select
            id="f-status"
            value={value.status?.[0] ?? ''}
            onChange={(e) =>
              onChange({
                ...value,
                status: e.target.value ? [e.target.value as WorkflowStatus] : undefined,
              })
            }
          >
            <option value="">Mind</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {hu.invoiceStatus[s]}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Label htmlFor="f-invoiceNumber">Számlaszám tartalmazza</Label>
          <Input
            id="f-invoiceNumber"
            placeholder="pl. SZ2026"
            value={value.invoiceNumber ?? ''}
            onChange={(e) => onChange({ ...value, invoiceNumber: e.target.value || undefined })}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label>Forrás</Label>
          <div className="flex flex-wrap gap-2 pt-1">
            {SOURCE_OPTIONS.map((s) => {
              const active = (value.source ?? []).includes(s)
              return (
                <Button
                  key={s}
                  type="button"
                  variant={active ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => onChange({ ...value, source: toggleArray(value.source, s) })}
                >
                  {hu.source[s]}
                </Button>
              )
            })}
          </div>
        </div>

        <div>
          <Label>Ország</Label>
          <div className="flex flex-wrap gap-2 pt-1">
            {COUNTRY_OPTIONS.map((c) => {
              const active = (value.countryType ?? []).includes(c)
              return (
                <Button
                  key={c}
                  type="button"
                  variant={active ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => onChange({ ...value, countryType: toggleArray(value.countryType, c) })}
                >
                  {hu.countryType[c]}
                </Button>
              )
            })}
          </div>
        </div>

        <div className="flex items-end gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              checked={!!value.hasDiscrepancy}
              onChange={(e) => onChange({ ...value, hasDiscrepancy: e.target.checked || undefined })}
            />
            Csak eltéréses
          </label>
        </div>

        <div className="flex items-end justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onReset}
            leftIcon={<RotateCcw className="h-4 w-4" />}
          >
            {hu.common.reset}
          </Button>
        </div>
      </div>

      {/* Költségfelosztás-szűrők (projekt / felelős / fizetésmód) */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label htmlFor="f-project">Projekt kód</Label>
          <Input
            id="f-project"
            placeholder="pl. PROJ-2026-A"
            value={value.projectCode ?? ''}
            onChange={(e) => onChange({ ...value, projectCode: e.target.value || undefined })}
          />
        </div>
        <div>
          <Label htmlFor="f-paidby">Fizetésmód</Label>
          <Select
            id="f-paidby"
            value={value.paidBy ?? ''}
            onChange={(e) =>
              onChange({ ...value, paidBy: (e.target.value as PaidBy) || undefined })
            }
          >
            <option value="">Mind</option>
            <option value="company">Céges</option>
            <option value="personal">Magán</option>
          </Select>
        </div>
        <div>
          <Label htmlFor="f-splituser">Felelős / résztvevő</Label>
          <Select
            id="f-splituser"
            value={value.splitUserId ?? ''}
            onChange={(e) => onChange({ ...value, splitUserId: e.target.value || undefined })}
          >
            <option value="">Mind</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.fullName}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  )
}
